import { api, apiForm, fetchBlob } from './client'

/**
 * 전자근로계약서 — 우리는 계약서를 **만들지도, 해석하지도 않는다.**
 *
 *   내용 검토      안 한다. 회사가 만든 PDF 를 그대로 나른다
 *   법적 유효성    판단하지 않는다. 우리가 남기는 건 "누가·언제·무엇에 서명했다"는 사실뿐
 *   교부           근로기준법상 교부 의무는 회사에 있다. 우리는 수단만 제공한다
 *
 * 우리가 실제로 하는 일은 딱 둘이다 — **바뀌지 않았음을 증명(sha256)** 하고,
 * **본인만 열게 하는 것**. 파일은 공개 URL 이 없어 반드시 서버를 거친다.
 */

export type ContractStatus = 'PENDING' | 'VIEWED' | 'SIGNED' | 'CANCELLED'

export const CONTRACT_STATUS_LABEL: Record<ContractStatus, string> = {
  PENDING: '전달됨',
  VIEWED: '열람함',
  SIGNED: '서명 완료',
  CANCELLED: '회수됨',
}

export interface MyContract {
  id: string
  title: string
  fileName: string
  fileSize: number
  fileHash: string
  status: ContractStatus
  viewedAt: string | null
  signedAt: string | null
  signatureHash: string | null
  createdAt: string
}

export interface AdminContract extends Omit<MyContract, 'signatureHash'> {
  employeeId: string
  signatureHash: string | null
  memo: string | null
  employee: {
    name: string
    employeeNumber: string | null
    department: { name: string } | null
  }
  uploadedBy: { name: string } | null
}

// ── 직원 ─────────────────────────────────────────────────────

export function getMyContracts() {
  return api<MyContract[]>('/api/contracts/my')
}

export function getMyPendingContractCount() {
  return api<{ count: number }>('/api/contracts/my/pending-count')
}

/** 손글씨 서명 PNG(data URL) 를 보낸다. */
export function signContract(id: string, signature: string) {
  return api<MyContract>(`/api/contracts/${id}/sign`, {
    method: 'POST',
    body: JSON.stringify({ signature }),
  })
}

// ── 관리자 ───────────────────────────────────────────────────

export function getContracts(params: {
  page?: number
  limit?: number
  status?: ContractStatus
  employeeId?: string
  search?: string
} = {}) {
  const q = new URLSearchParams()
  if (params.page) q.set('page', String(params.page))
  if (params.limit) q.set('limit', String(params.limit))
  if (params.status) q.set('status', params.status)
  if (params.employeeId) q.set('employeeId', params.employeeId)
  if (params.search) q.set('search', params.search)
  return api<{ items: AdminContract[]; total: number; page: number; limit: number }>(
    `/api/contracts?${q.toString()}`,
  )
}

export function uploadContract(input: {
  employeeId: string
  title: string
  memo?: string
  file: File
}) {
  const form = new FormData()
  form.append('employeeId', input.employeeId)
  form.append('title', input.title)
  if (input.memo) form.append('memo', input.memo)
  form.append('file', input.file)
  return apiForm<AdminContract>('/api/contracts', form)
}

export function cancelContract(id: string) {
  return api<AdminContract>(`/api/contracts/${id}/cancel`, { method: 'PATCH' })
}

// ── 파일 열람 ────────────────────────────────────────────────

/**
 * PDF 를 blob 으로 받아 화면에서 열 수 있는 URL 을 만든다.
 *
 * 주소창 이동(`window.open('/api/contracts/…')`)을 쓰지 않는 이유 —
 * 이 엔드포인트는 Authorization 헤더를 요구하므로 401 이 나고, 토큰을
 * 쿼리스트링에 실으면 서버 로그·브라우저 히스토리에 액세스 토큰이 남는다.
 *
 * 반환한 URL 은 호출부가 다 쓰고 `URL.revokeObjectURL` 해야 한다.
 */
export async function openContractFile(
  id: string,
): Promise<{ url: string } | { error: string }> {
  const res = await fetchBlob(`/api/contracts/${id}/file`)
  if ('error' in res) return res
  return { url: URL.createObjectURL(res.blob) }
}

export function fileSizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`
}
